import TableRowActionMenu from '../../BaseComponents/TableRowActionMenu';
import { useDispatch, useSelector } from 'react-redux';
import {
  changeDeleteModalVisibility,
  changeModalVisibility,
  changeViewAppointmentResultsModalVisibility,
  setAppointmentIdToEdit,
  setSelectedAppointmentIdToView,
  setAppointmentsList,
} from '../../../store/memberSlice';
import { formatInTimeZone } from 'date-fns-tz';
import './Appointments.scss';
import './AppointmentsTable.scss';
import { ReactComponent as FileIcon } from '../../../assets/icons/File.svg';
import {
  AppScenariosAppointmentBasePayloadAppointmentInResponse,
  PharmaInfo,
  AppointmentAssigneeType,
  LocationType,
  AppointmentStatus,
} from '../../../types/apiGeneratedTypes';
import AppointmentInlineStatusSelect from '../../Appointments/AppointmentInlineStatusSelect';
import { RootState } from '../../../store/store';
import { getLocationTypeLabel } from '../../../utils/locationType';

interface ExtendedAppointmentInResponse
  extends AppScenariosAppointmentBasePayloadAppointmentInResponse {
  pharmacy?: PharmaInfo;
}

type Props = {
  appointmentData: ExtendedAppointmentInResponse;
  isUpcomingTab?: boolean;
  currentSelectedFilter?: 'all' | 'upcoming' | 'past' | 'future-tests' | 'pmp-alerts';
  onInuviBookingClick: (appointmentId: number) => Promise<void>;
  doctorNameById?: Record<string, string>;
  nutritionistNameById?: Record<string, string>;
  setIsAppointmentView: (isViewOnly: boolean) => void;
};

const AppointmentsTableRow = ({
  appointmentData,
  isUpcomingTab = false,
  currentSelectedFilter = 'all',
  onInuviBookingClick,
  doctorNameById,
  nutritionistNameById,
  setIsAppointmentView
}: Props) => {
  const dispatch = useDispatch();
  const { appointmentsList } = useSelector(
    (state: RootState) => state.member
  );

  const onOptionClick = (id: number, action: string) => {
    if (action === 'edit' || action === 'view') {
      setIsAppointmentView(action === 'view');
      dispatch(setAppointmentIdToEdit(id));
      return dispatch(changeModalVisibility(true));
    }
    if (action === 'delete') {
      dispatch(setAppointmentIdToEdit(id));
      return dispatch(changeDeleteModalVisibility(true));
    }
    if (action === 'inuvi-booking') {
      return onInuviBookingClick(id);
    }
  };
  const assigned_doctor = JSON.parse(sessionStorage.getItem("assigned_doctor") ?? "false");
  const { admin: { role } } = useSelector((state: RootState) => state.admin);
  const visible = ((role !== "doctor") || (assigned_doctor && role === "doctor"));

  const assigneeType = appointmentData.assignee_type;
  const assigneeId = appointmentData.assignee_id;

  const getAssigneeName = () => {
    if (!assigneeType || !assigneeId) return undefined;

    if (assigneeType === AppointmentAssigneeType.NUTRITIONIST) {
      return nutritionistNameById?.[String(assigneeId)];
    }
    if (assigneeType === AppointmentAssigneeType.DOCTOR) {
      return doctorNameById?.[String(assigneeId)];
    }
    return undefined;
  };

  const assigneeName = getAssigneeName();

  // Create options array based on appointment type
  const getActionOptions = () => {
    const baseOptions = [
      { key: 'view', value: 'View' },
      { key: 'edit', value: 'Edit' },
      { key: 'delete', value: 'Delete' }
    ];

    // Add "Inuvi Booking" action for TULI appointments
    if (appointmentData.location === LocationType.TULI) {
      baseOptions.push({ key: 'inuvi-booking', value: 'Inuvi Booking' });
    }

    return baseOptions;
  };

  return (
    <div className='table-row'>
      <div className='status'>
        {appointmentData.id != null ? (
          <AppointmentInlineStatusSelect
            appointmentId={appointmentData.id}
            status={appointmentData.status}
            disabled={!visible}
            onStatusSaved={(newStatus: AppointmentStatus) => {
              dispatch(
                setAppointmentsList(
                  appointmentsList.map((a) =>
                    a.id === appointmentData.id ? { ...a, status: newStatus } : a
                  )
                )
              );
            }}
          />
        ) : (
          '—'
        )}
      </div>
      <div className='type'>
        {getLocationTypeLabel(appointmentData.location)}
      </div>
      <div className='date'>
        {formatInTimeZone(appointmentData.scheduled_time, 'UTC', 'dd/MM/yyyy')}
      </div>
      <div className='time'>
        {formatInTimeZone(
          appointmentData.scheduled_time,
          'Europe/London',
          'hh:mm a'
        )}
      </div>
      <div className='test-names'>{appointmentData.title}</div>
      <div className='assignee'>
        {assigneeType && assigneeId ? (
          <div className='assignee-display'>
            <span className='assignee-type-tag'>{assigneeType}</span>
            <span className='assignee-name'>{assigneeName || '—'}</span>
          </div>
        ) : (
          '—'
        )}
      </div>
      <div className='location'>
        {appointmentData.pharmacy ? (
          <div className='pharmacy-info'>
            <div className='pharmacy-name'>
              {appointmentData.pharmacy.name}
              <span className='location-tag'>{appointmentData.location}</span>
            </div>
            <div className='pharmacy-address'>
              {appointmentData.pharmacy.address1},{' '}
              {appointmentData.pharmacy.city}
            </div>
            <div className='pharmacy-phone'>
              {appointmentData.pharmacy.phone}
            </div>
          </div>
        ) : appointmentData.location === LocationType.MEMBER_HOME ? (
          <div className='clinic-info'>
            <div className='pharmacy-name'>
              <span className='location-tag'>{appointmentData.location}</span>
            </div>
          </div>
        ) : (
          <div className='clinic-info'>
            <div className='pharmacy-name'>
              {appointmentData.clinic?.title}
              <span className='location-tag'>{appointmentData.location}</span>
            </div>
            <div className='pharmacy-address'>
              {appointmentData.clinic?.address}
            </div>
            <div className='pharmacy-phone'>
              {appointmentData.clinic?.phone}
            </div>
          </div>
        )}
      </div>
      <div className='meeting-url'>
        {appointmentData.meeting_url && (
          <a
            className='meeting-link'
            href={appointmentData.meeting_url}
            target='_blank'
            rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
          >
            Meeting Link
          </a>
        )}
      </div>
      <div className='appointment-id'>{appointmentData.unique_id}</div>
      <div className='test-results'>
        {(currentSelectedFilter === 'past' ||
          (currentSelectedFilter === 'all' &&
            new Date(appointmentData.scheduled_time) < new Date())) && visible && (
            <div
              className='test-results-button'
              onClick={() => {
                dispatch(setSelectedAppointmentIdToView(appointmentData.id));
                dispatch(changeViewAppointmentResultsModalVisibility(true));
              }}
            >
              <FileIcon />
              View results
            </div>
          )}
      </div>
      <div className='actions'>
        {visible && <TableRowActionMenu
          options={getActionOptions()}
          id={appointmentData.id}
          onActionMenuClick={onOptionClick}
        />}
      </div>
    </div>
  );
};

export default AppointmentsTableRow;
